import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { OrderStopType } from "@/constants/order";
import type { RouteStopDto, TravelOrderDto, TripOverview, TripRouteDto } from "@/api/trips/dto/trip.dto";
import {
  FinishRouteStatuses,
  NotStartedRouteStopStatus,
  RouteStopState,
  RouteStopStatus,
  TravelOrderCancelStatuses,
  TripDateField,
} from "@/constants/trip";
import type { TripMonitorItem } from "@/api/trips/dto/trip-monitor";
import dayjs, { type Dayjs } from "dayjs";
import type { ContactPersonDto, TruckDriverDto, TruckDto, TruckOwnerDto } from "@/api/trucks/dto/truck.dto";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import type { OwnerDto } from "@/api/users/dto/owner.dto";
import { useError } from "@/composables/useError";
import { useBrokerUpdate } from "@/composables/useBrokerUpdate";
import { useCheckCall } from "@/composables/useCheckCall";
import type { TripOrderStop, TripRouteStop, TripStop } from "@/types/trip";

export function useTrip() {
  const api = new API();
  const { showAPIError } = useError();
  const { createMonitorBrokerUpdateContext, isBrokerUpdateTimerDisabled } = useBrokerUpdate();
  const { isCheckCallDisabled } = useCheckCall();

  function numerateAllRouteStops(routeStops: RouteStopDto[]): RouteStopDto[] {
    let pickupNum = 1;
    let deliveryNum = 1;
    let reloadPickupNum = 1;
    let reloadDeliveryNum = 1;

    return routeStops.map((stop) => {
      if (stop.isReload) {
        if (stop.type === OrderStopType.pickup) {
          return { ...stop, number: reloadPickupNum++ };
        }
        if (stop.type === OrderStopType.delivery) {
          return { ...stop, number: reloadDeliveryNum++ };
        }
      } else {
        if (stop.type === OrderStopType.pickup) {
          return { ...stop, number: pickupNum++ };
        }
        if (stop.type === OrderStopType.delivery) {
          return { ...stop, number: deliveryNum++ };
        }
      }

      return stop;
    });
  }

  async function createTripOverview(tripId: number): Promise<TripOverview | undefined> {
    const api = new API();

    const { showSnackbarError } = useSnackbarStore();

    try {
      const response = await api.trips.getTripById(tripId);
      const tripOverview = response.data;

      if (!tripOverview) return;

      const updatedRoutes = await Promise.all(
        tripOverview.routes.map(async (route) => {
          let truck: TruckDto | undefined = undefined;

          if (route.truckId) {
            const truckDtoResponse = await api.trucks.findTruckById(route.truckId);

            truck = truckDtoResponse?.data;
          }

          const updatedTravelOrders = await Promise.all(
            route.travelOrders.map(async (travelOrder) => {
              const orderResponse = await api.orders.findOrderById(travelOrder.orderId);
              if (!orderResponse.success || !orderResponse.data) {
                return travelOrder;
              }

              return { ...travelOrder, order: orderResponse.data };
            }),
          );

          let driver: DriverDto | undefined = undefined;

          if (route.driverId) {
            const driverResponse = await api.users.findDriverById(route.driverId);

            driver = driverResponse?.data;
          }

          const updatedStops = numerateAllRouteStops(route.routeStops);

          return {
            ...route,
            truck: truck,
            travelOrders: updatedTravelOrders,
            routeStops: updatedStops,
            driver,
          };
        }),
      );

      return {
        ...tripOverview,
        routes: updatedRoutes,
      };
    } catch (error) {
      console.error(error);
      showSnackbarError(`Could not create trip overview ${error}`);
    }
  }

  function getLoadedMiles(routeDistance: Record<string, number>) {
    return Object.values(routeDistance).reduce((sum, distance) => distance + sum, 0);
  }

  function createEmptyRouteStop(): RouteStopDto {
    return {
      position: 1,
      type: OrderStopType.pickup,
      isReload: false,
      facilityId: 0,
      state: RouteStopState.normal,
      orderStopId: null,
      freights: [],
      timezone: "",
      timeType: null,
      timeFrom: null,
      timeTo: "",
      status: RouteStopStatus.notStarted,
      checkInTime: null,
      loadUnloadTime: null,
      verifyTime: null,
      verifyDispatcherId: null,
      podSignedBy: null,
      checkOutTime: null,
      bolFiles: [],
      podFiles: [],
      otherFiles: [],
      bols: [],
      order: null,
      oldFacilityId: null,
      number: 0,
      tripStopId: "",
      isDeleted: false,
    };
  }

  function isTravelOrderCancelled(travelOrder: TravelOrderDto): boolean {
    return TravelOrderCancelStatuses.includes(travelOrder.status);
  }

  function isRouteStopCancelled(route: TripRouteDto, routeStop: RouteStopDto): boolean {
    const travelOrderIds = new Set(routeStop.travelOrderIds ?? []);
    const relatedTravelOrders = route.travelOrders.filter((travelOrder) => {
      return travelOrder.id !== undefined && travelOrderIds.has(travelOrder.id);
    });

    return Boolean(relatedTravelOrders.length) && relatedTravelOrders.every(isTravelOrderCancelled);
  }

  function isRouteStopActive(routeStop: TripRouteStop) {
    return !routeStop.isDeleted && routeStop.use && !routeStop.$isCancelled;
  }

  function getExtremeDate(values: (string | null)[], type: "min" | "max"): Dayjs | null {
    const validDates = values.filter((v) => v);

    if (!validDates.length) {
      return null;
    }

    const dayjsValues = validDates.map((v) => dayjs(v));

    const minValue = dayjs.min(dayjsValues);
    const maxValue = dayjs.max(dayjsValues);

    if (!minValue || !maxValue) {
      return null;
    }

    return type === "min" ? minValue : maxValue;
  }

  function completeFormTrips(trips: TripMonitorItem[]): TripMonitorItem[] {
    return trips.map((trip) => {
      const activeRoutes = trip.routes.filter((route) => !FinishRouteStatuses.includes(route.status));

      const nextStopTimes = activeRoutes.map((r) => r.nextRouteStop?.timeTo || null);
      const etaTimes = activeRoutes.map((r) => r.lastCheckCall?.eta || null);
      const checkCallTimes = activeRoutes.map((r) => r.lastCheckCall?.checkCallTime || null);
      const checkCallTimerTimers = activeRoutes.map((r) => r.nextCheckCallTime);
      const updateTimerTimes = activeRoutes.flatMap((r) => r.travelOrders.map((to) => to.nextUpdateTime));
      const enabledCheckCallTimerTimes = activeRoutes
        .filter((route) => !isCheckCallDisabled(route))
        .map((route) => route.nextCheckCallTime);
      const enabledUpdateTimerTimes = activeRoutes
        .flatMap((route) =>
          route.travelOrders.filter((to) => {
            const brokerUpdateDisableTimerContext = createMonitorBrokerUpdateContext(route, to, trip.orders);

            return !isBrokerUpdateTimerDisabled(brokerUpdateDisableTimerContext);
          }),
        )
        .map((to) => to.nextUpdateTime);

      const nearestTimerDate =
        getExtremeDate([...enabledCheckCallTimerTimes, ...enabledUpdateTimerTimes], "min")?.toISOString() ?? null;

      return {
        ...trip,
        nextStopSoonestDate: getExtremeDate(nextStopTimes, "min")?.toISOString() ?? null,
        nextStopLatestDate: getExtremeDate(nextStopTimes, "max")?.toISOString() ?? null,
        etaSoonestDate: getExtremeDate(etaTimes, "min")?.toISOString() ?? null,
        etaLatestDate: getExtremeDate(etaTimes, "max")?.toISOString() ?? null,
        lastCheckCallSoonestDate: getExtremeDate(checkCallTimes, "min")?.toISOString() ?? null,
        lastCheckCallLatestDate: getExtremeDate(checkCallTimes, "max")?.toISOString() ?? null,
        checkCallTimersSoonestDate: getExtremeDate(checkCallTimerTimers, "min")?.toISOString() ?? null,
        checkCallTimerLatestDate: getExtremeDate(checkCallTimerTimers, "max")?.toISOString() ?? null,
        updateTimerSoonestDate: getExtremeDate(updateTimerTimes, "min")?.toISOString() ?? null,
        updateTimerLatestDate: getExtremeDate(updateTimerTimes, "max")?.toISOString() ?? null,
        allTimersSoonestDate: nearestTimerDate,
        allTimersLatestDate: nearestTimerDate,
      };
    });
  }

  function sortTripsByDate(
    trips: TripMonitorItem[],
    field: TripDateField,
    mode: "soonest" | "latest" = "soonest",
  ): TripMonitorItem[] {
    return [...trips].sort((a, b) => {
      const aDateStr = a[field];
      const bDateStr = b[field];

      if (!aDateStr && !bDateStr) {
        return 0;
      }

      if (!aDateStr) {
        return 1;
      }

      if (!bDateStr) {
        return -1;
      }

      const aTime = dayjs(aDateStr);
      const bTime = dayjs(bDateStr);

      return mode === "latest" ? bTime.diff(aTime) : aTime.diff(bTime);
    });
  }

  async function getRouteDriver(
    routeDriverId: number | null,
    truck: TruckDto | null | undefined,
  ): Promise<DriverDto | TruckDriverDto | null> {
    const shouldUseTruckDriver = routeDriverId === truck?.driverId || (!routeDriverId && truck?.driver);

    if (shouldUseTruckDriver) {
      return truck.driver;
    }

    if (!routeDriverId) {
      return null;
    }

    try {
      const response = await api.users.findDriverById(routeDriverId);

      if (response.errorData) {
        showAPIError(response);
        return null;
      }

      return response.data || null;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  async function getRouteDriver2(
    routeDriver2Id: number | null,
    truck: TruckDto | null | undefined,
  ): Promise<DriverDto | TruckDriverDto | null> {
    const shouldUseTruckDriver2 = routeDriver2Id === truck?.driver2Id || (!routeDriver2Id && truck?.driver2);

    if (shouldUseTruckDriver2) {
      return truck?.driver2;
    }

    if (!routeDriver2Id) {
      return null;
    }

    try {
      const response = await api.users.findDriverById(routeDriver2Id);

      if (response.errorData) {
        showAPIError(response);
        return null;
      }

      return response.data || null;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  async function getRouteContactPerson(
    contactPersonId: number | null | undefined,
    truck: TruckDto | null | undefined,
  ): Promise<ContactPersonDto | TruckDriverDto | null> {
    const shouldUseTruckDriver =
      contactPersonId === truck?.driver?.contactPersonId || (!contactPersonId && truck?.driver?.contactPerson);

    if (shouldUseTruckDriver) {
      return truck?.driver?.contactPerson || null;
    }

    if (!contactPersonId) {
      return null;
    }

    try {
      const response = await api.users.findUserById(contactPersonId);

      if (response.errorData) {
        showAPIError(response);
        return null;
      }

      return response.data ? (response.data as unknown as ContactPersonDto) : null;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  async function getRouteOwner(
    routeOwnerId: number | null,
    truck: TruckDto | null | undefined,
  ): Promise<OwnerDto | TruckOwnerDto | null> {
    const shouldUseTruckInfo = routeOwnerId === truck?.ownerId || (!routeOwnerId && truck?.owner);

    if (shouldUseTruckInfo) {
      return truck.owner;
    }

    if (!routeOwnerId) {
      return null;
    }

    try {
      const response = await api.users.findOwnerById(routeOwnerId);

      if (response.errorData) {
        showAPIError(response);
        return null;
      }

      return response.data || null;
    } catch (error) {
      console.error(error);

      return null;
    }
  }

  function checkIsRouteStopProcessStarted(routeStop: TripRouteStop) {
    return !NotStartedRouteStopStatus.includes(routeStop.status);
  }

  function createStopNumber(
    stopId: string | null | undefined,
    stops: (TripOrderStop | TripStop | TripRouteStop)[],
  ): number | null {
    if (!stopId) {
      return null;
    }

    let deliveryNum = 0;
    let pickupNum = 0;
    let reloadNumber = 0;

    const sorted = [...stops].sort((a, b) => a.position - b.position);

    for (const stop of sorted) {
      if (stop.type === OrderStopType.reload) {
        reloadNumber++;

        if (stop.id === stopId) {
          return reloadNumber;
        }
      }

      if (stop.type === OrderStopType.delivery) {
        deliveryNum++;

        if (stop.id === stopId) {
          return deliveryNum;
        }
      }

      if (stop.type === OrderStopType.pickup) {
        pickupNum++;

        if (stop.id === stopId) {
          return pickupNum;
        }
      }
    }

    return null;
  }

  return {
    createTripOverview,
    getLoadedMiles,
    createEmptyRouteStop,
    isTravelOrderCancelled,
    isRouteStopCancelled,
    completeFormTrips,
    sortTripsByDate,
    getRouteDriver,
    getRouteContactPerson,
    getRouteOwner,
    getRouteDriver2,
    checkIsRouteStopProcessStarted,
    createStopNumber,
    isRouteStopActive,
  };
}
